
import OpenEngineerApi from '@/api/OpenEngineerApi';
import { defineComponent, onMounted, reactive, ref, toRefs, watch, createVNode, inject, computed, nextTick } from 'vue';
import { EllipsisOutlined, ExclamationCircleOutlined } from '@ant-design/icons-vue'
import { message, Modal } from 'ant-design-vue';
import router from '@/router';
import OpenOtherApi from '@/api/OpenOtherApi';
import { ProviderModule } from '@/store/modules/provider.module';

interface DataType {
  empty: boolean,
  list: Array<any>,
  recordCount: number
}

enum enumSort {
  'user.name' = 'SORT_NAME',
  'defaultServiceDeskName' = 'SORT_SERVICEDESK',
  'role.name' = 'SORT_ROLE',
  'user.mobilePhone' = 'SORT_PHONE',
  'user.email' = 'SORT_EMAIL',
  'user.lastLoginAt' = 'SORT_TIME',
  'user.externalId' = 'SORT_EXTERNALID',
  'user.nickname' = 'SORT_NICKNAME'
}

const emumStatus = {
  0: '已停用',
  1: '已启用',
  2: '待激活'
}

const defaultColumns = JSON.stringify([
  { title: '姓名', width: 120, dataIndex: 'user.name', fixed: 'left', ellipsis: true, sorter: true },
  { title: '昵称', width: 120, dataIndex: 'user.nickname', ellipsis: true, sorter: true },
  { title: '首选部门', width: 200, dataIndex: 'defaultServiceDeskName', ellipsis: true, sorter: true },
  { title: '账号状态', width: 100, dataIndex: 'user.status' },
  { title: '客服角色', width: 180, dataIndex: 'role.name', ellipsis: true, sorter: true },
  { title: '手机', width: 180, dataIndex: 'user.mobilePhone', ellipsis: true, sorter: true },
  { title: 'Email', width: 200, dataIndex: 'user.email', ellipsis: true, sorter: true },
  { title: 'ExternalId', width: 180, dataIndex: 'user.externalId', sorter: true },
  { title: '最近登录', width: 180, dataIndex: 'user.lastLoginAt', ellipsis: true, sorter: true },
  { title: '启用账号', dataIndex: 'toggle', fixed: 'right', width: 100 },
  { title: '操作', dataIndex: 'operation', fixed: 'right', width: 60 }
])

export default defineComponent({
  components: {
    EllipsisOutlined
  },
  props: {
    selectedRows: {
      type: Array,
      require: true
    },
    engineerQuery: {
      type: Object,
      default: () => ({} as any),
      required: true
    },
    hadSunloginWeb: {
      type: Boolean,
      default: false
    }
  },
  setup (props, ctx) {
    const { engineerQuery } = toRefs(props)
    const page = reactive({
      pageNumber: 1,
      pageSize: 10
    })
    const total = ref(0)
    const data = ref<Array<any>>([])
    const selectedRowKeys = ref<Array<number>>([])
    const columns = ref(JSON.parse(defaultColumns))
    const wrapRef = ref<HTMLDivElement>();
    const tabRef = ref<HTMLDivElement>()
    const hasPermission: any = inject('hasPermission')
    const currentRoleNameKey = computed(() => ProviderModule.engineer.role.nameKey)

    watch(
      () => props.selectedRows,
      (n) => {
        if (!n) return
        selectedRowKeys.value = n.map((item: any) => item.id as number)
      }
    )

    const rowSelection = {
      selectedRowKeys: selectedRowKeys,
      onChange: (selectedRowKey: Array<number>, selectedRow: Array<any>) => {
        ctx.emit('update:selectedRows', selectedRow)
      },
      getCheckboxProps: (record) => {
        if (record.role.nameKey === 'ROLE_NAME_CREATOR') {
          return { disabled: true }
        } else if (record.role.nameKey === 'ROLE_NAME_ADMIN' && (currentRoleNameKey.value === 'ROLE_NAME_ENGINEER' || !currentRoleNameKey.value)) {
          return { disabled: true }
        } else {
          return { disabled: false }
        }
       }
    };

    function handleSwitch (record) {
      const status = record.user?.status
      if (status === 2) { // 状态为待激活就不处理
        return
      }
      const enabled = status === 0 // 状态为0就是要启用
      Modal.confirm({
        title: () => '确认要' + (enabled ? '启用' : '停用') + '该客服吗？',
        icon: () => createVNode(ExclamationCircleOutlined),
        okText: '确定',
        cancelText: '取消',
        onOk () {
          OpenEngineerApi.batchEnable({
            engineerIds: [record.id],
            enabled: enabled
          }).then(() => {
            message.success(enabled ? '已启用' : '已停用')
            record.user.status = enabled ? 1 : 0
          })
        }
      })
    }

    const orderBy = reactive({
      pageOrderType: 'DESC',
      pageSort: 'SORT_TIME'
    })

    function tableChange (pagination, filters, sorter) {
      const { field, order } = sorter
      orderBy.pageSort = enumSort[field]
      orderBy.pageOrderType = order === "ascend" ? 'ASC' : 'DESC'
      loadData()
    }

    // 加载用户数据
    function loadEngineer () {
      return OpenEngineerApi.multiCondition({
        page,
        status: engineerQuery.value?.status,
        engineerQuery: {
          defaultServiceDeskId: engineerQuery.value?.defaultServiceDeskId,
          serviceDeskId: engineerQuery.value?.serviceDeskId,
          email: engineerQuery.value?.email,
          name: engineerQuery.value?.name,
          phone: engineerQuery.value?.phone,
          roleId: engineerQuery.value?.roleId,
          externalId: engineerQuery.value?.externalId,
          searchKey: engineerQuery.value?.searchKey,
          searchType: engineerQuery.value?.searchType,
          pageOrderType: orderBy.pageOrderType,
          pageSort: orderBy.pageSort
        }
      })
        .then((res: DataType) => {
          if (res.list.length === 0 && page.pageNumber > 1) {
            page.pageNumber -= 1
            return
          }
          data.value = res.list;
          total.value = res.recordCount;
          return data.value.map(item => item.userId).join(',')
        }).catch(() => '')
    }
    // 加载自定义字段
    function loadCustomField () {
      return OpenOtherApi.getCustomField('engineer')
        .then(data => {
          const initColumns = JSON.parse(defaultColumns)
          const customFieldArray:any[] = []
          const customFieldId: number[] = []
          for (let i = 0; i < data.length; i++) {
            if (data[i].active) {
              customFieldArray.push({ title: data[i].title, dataIndex: data[i].id, width: data[i].defaultLength, ellipsis: true })
              customFieldId.push(data[i].id)
            }
          }
          initColumns.splice(initColumns.length - 2, 0, ...customFieldArray)
          columns.value = initColumns
          return customFieldId.join(',')
        }).catch(() => '')
    }

    function loadCustomFieldValue (userIds, customFieldIds) {
      OpenOtherApi.getCustomFieldValue({
        userIds,
        customFieldIds
      }).then(customFieldValue => {
        data.value.forEach(enginner => {
          const filterCustomFieldValue = customFieldValue.filter(cv => cv.userId === enginner.userId)
          filterCustomFieldValue.forEach(cv => {
            enginner[cv.customFieldId] = cv.value
          })
        })
      })
    }

    function loadData () {
      Promise.all([
        loadEngineer(),
        loadCustomField()
      ]).then(res => {
        if (res[0] === '' || res[1] === '') return
        loadCustomFieldValue(res[0], res[1])
      })
    }

    function handleDelete (record) {
      Modal.confirm({
        title: () => '确认要删除该客服吗？',
        icon: () => createVNode(ExclamationCircleOutlined),
        okText: '确定',
        cancelText: '取消',
        onOk () {
          OpenEngineerApi.deleteEngineers({
            ids: record.id
          }).then(() => {
            message.success('已删除')
            loadData()
          })
        }
      })
    }

    function handleEdit (record) {
      router.push({ path: '/system_setting/account-add/detail', query: { id: record.id, userId: record.user.id } });
    }

    function handlePageChange (pageNumber, pageSize) {
      if (page.pageSize !== pageSize) {
        page.pageNumber = 1
        page.pageSize = pageSize
      } else if (page.pageNumber !== pageNumber) {
        page.pageNumber = pageNumber
      }
      loadData()
    }

    watch(
      () => engineerQuery.value,
      (n) => {
        localStorage.setItem('engineer_senior_search', JSON.stringify(n))
        page.pageNumber = 1;
        loadData()
      }, {
        deep: true
      }
    )

    function resizeTab () {
      nextTick(() => {
        if (!tabRef.value || !wrapRef.value) return
        tabRef.value.style.width = wrapRef.value.offsetWidth + 'px'
      })
    }

    onMounted(() => {
      loadData()
      resizeTab()
      window.addEventListener('resize', function () {
        resizeTab()
      })
    })

    return {
      emumStatus,
      page,
      total,
      data,
      rowSelection,
      columns,
      wrapRef,
      tabRef,
      hasPermission,
      tableChange,
      handleSwitch,
      handleEdit,
      handlePageChange,
      handleDelete,
      loadData
    }
  }
})
